import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { ProtectedRoute } from '@/routes/ProtectedRoute';
import VendorRoute from '@/routes/VendorRoute';
import { SuperAdminRoute } from '@/routes/SuperAdminRoute';
import { IrancharsouqAdminRoute } from '@/routes/IrancharsouqAdminRoute';
import { InspectorRoute } from '@/routes/InspectorRoute';
import Unauthorized from '@/routes/Unauthorized';

import { TraceabilityInspectorPage } from '@/features/traceability';
import Login from '@/features/auth/components/Login';
import Signup from '@/features/auth/components/Signup';
import SelectRolePage from '@/features/auth/components/SelectRolePage';
import ForgotPassword from '@/features/auth/components/ForgotPassword';
import Home from '@/features/content/components/Home';
import AboutUs from '@/features/content/components/AboutUs';
import ContactUs from '@/features/content/components/ContactUs';
import ReferralLanding from '@/features/referral-landing/ReferralLanding';
import Storefront from '@/features/products/components/Storefront';
import ProductDetailView from '@/features/products/components/ProductDetailView';
import VendorStorefront from '@/features/marketplace/components/VendorStorefront';
import Marketplace from '@/features/marketplace/components/Marketplace';
import BranchStorefront from '@/features/marketplace/components/BranchStorefront';
import { useMarketplace } from '@/features/marketplace/context/marketplaceContext';
import VendorReferralConfigsPage from '@/features/vendor/components/simulator/VendorReferralConfigsPage';
import VendorReferralRoute from '@/routes/VendorReferralRoute';
import CartPage from '@/features/cart/components/CartPage';
import CheckoutPage from '@/features/orders/components/CheckoutPage';
import OrderHistory from '@/features/orders/components/OrderHistory';
import OrderDetail from '@/features/orders/components/OrderDetail';
import PaymentResultPage from '@/features/orders/components/PaymentResultPage';
import UserProfile from '@/features/customers/components/UserProfile';
import CustomerProfileLayout from '@/features/customers/components/CustomerProfileLayout';
import WalletPage from '@/features/customers/wallet/WalletPage';
import BasketSimulatorPage from '@/features/customers/basket-simulator/BasketSimulatorPage';
import MyCompanies from '@/features/customers/components/MyCompanies';
import ContractBasketPage from '@/features/customers/components/ContractBasketPage';
import ReferralTreePage from '@/features/customers/components/ReferralTreePage';
import SuperAdminLayout from '@/features/super-admin/components/layout/SuperAdminLayout';
import SuperAdminProfile from '@/features/super-admin/components/profile/SuperAdminProfile';
import SuperAdminDashboard from '@/features/super-admin/components/dashboard/SuperAdminDashboard';
import AdminManagementTable from '@/features/super-admin/components/admins/AdminManagementTable';
import UserManagementTable from '@/features/super-admin/components/users/UserManagementTable';
import VendorApprovalTable from '@/features/super-admin/components/vendors/VendorApprovalTable';
import AdminOrderList from '@/features/super-admin/components/orders/AdminOrderList';
import SmsCampaigns from '@/features/super-admin/components/announcements/SmsCampaigns';
import {
    PaymentsPage, VendorSettlementsPage, VendorSettlementDetailPage, PayoutHistoryPage,
    RefundsPage, ShippingPayablesPage, SellerGatewayAdminPage, AdminAuditLogPage,
} from '@/features/super-admin/settlements/SettlementPages';
import ReferralSimulator from '@/features/super-admin/referral-simulator/ReferralSimulator';
import CommissionsPage from '@/features/super-admin/commissions/CommissionsPage';
import ReferralConfigPage from '@/features/super-admin/referral-config/ReferralConfigPage';
import SuperAdminContractsPage from '@/features/super-admin/contracts/SuperAdminContractsPage';
import IrancharsouqAdminLayout from '@/features/irancharsouq-admin/components/layout/IrancharsouqAdminLayout';
import IrancharsouqAdminDashboard from '@/features/irancharsouq-admin/components/dashboard/IrancharsouqAdminDashboard';
import AdminProducts from '@/features/irancharsouq-admin/components/products/AdminProducts';
import AdminOrders from '@/features/irancharsouq-admin/components/orders/AdminOrders';
import AdminProfile from '@/features/irancharsouq-admin/components/profile/AdminProfile';

// Vendor Imports
import VendorLayout from '@/features/vendor/components/layout/VendorLayout';
import VendorDashboard from '@/features/vendor/components/dashboard/VendorDashboard';
import VendorProducts from '@/features/vendor/components/products/VendorProducts';
import VendorProductForm from '@/features/vendor/components/products/VendorProductForm';
import VendorOrders from '@/features/vendor/components/orders/VendorOrders';
import VendorProfile from '@/features/vendor/components/profile/VendorProfile';
import SimulationProductsPage from '@/features/vendor/components/simulator/SimulationProductsPage';
import VendorPackagesList from '@/features/vendor/components/simulator/VendorPackagesList';
import VendorReferralSimulator from '@/features/vendor/components/simulator/ReferralSimulator';
import VendorContractsPage from '@/features/vendor/components/contracts/VendorContractsPage';
import VendorCommissionsPage from '@/features/vendor/components/commissions/VendorCommissionsPage';
import VendorReportsPage from '@/features/vendor/components/reports/VendorReportsPage';
import VendorPackageBuyersPage from '@/features/vendor/components/reports/VendorPackageBuyersPage';
// 👈 [ADDED] ایمپورت صفحه مبنای محاسبات
import VendorPricingRulesPage from '@/features/vendor/components/pricing/VendorPricingRulesPage';

import ReportsLayout, { ReportsIndexRedirect } from '@/features/reports/ReportsLayout';
import PlatformOnly from '@/features/reports/PlatformOnly';
import SalesReport from '@/features/reports/pages/SalesReport';
import ProductsReport from '@/features/reports/pages/ProductsReport';
import CategoriesReport from '@/features/reports/pages/CategoriesReport';
import InventoryReport from '@/features/reports/pages/InventoryReport';
import OrdersFunnelReport from '@/features/reports/pages/OrdersFunnelReport';
import FinanceReport from '@/features/reports/pages/FinanceReport';
import CustomersReport from '@/features/reports/pages/CustomersReport';
import VendorsReport from '@/features/reports/pages/VendorsReport';
import CompanyRoute from '@/routes/CompanyRoute';
import CompanyLayout from '@/features/company/components/CompanyLayout';
import CompanyDashboard from '@/features/company/pages/CompanyDashboard';
import CompanyEmployees from '@/features/company/pages/CompanyEmployees';
import CompanyAddEmployee from '@/features/company/pages/CompanyAddEmployee';
import CompanyWallet from '@/features/company/pages/CompanyWallet';
import CompanyContracts from '@/features/company/pages/CompanyContracts';

function CustomerProfileRedirect() {
    const location = useLocation();
    return <Navigate to="/profile" replace state={location.state} />;
}

function LegacyOrderRedirect() {
    const { orderId } = useParams();
    return <Navigate to={`/profile/orders/${orderId}`} replace />;
}

export default function AppRoutes() {
    const { isVendorSubdomain } = useMarketplace();

    return (
        <Routes>
            <Route path="/" element={ isVendorSubdomain ? <VendorStorefront /> : <Home /> } />
            <Route path="/products" element={<Storefront />} />
            <Route path="/vendors/:vendorId" element={<VendorStorefront />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/branches/:branchId" element={<BranchStorefront />} />
            <Route path="/product/:id" element={<ProductDetailView />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/select-role" element={<SelectRolePage />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/ref/:code" element={<ReferralLanding />} />

            <Route path="/profile" element={<ProtectedRoute requiredRole="customer"><CustomerProfileLayout /></ProtectedRoute>}>
                <Route index element={<UserProfile />} />
                <Route path="orders" element={<OrderHistory />} />
                <Route path="orders/:orderId" element={<OrderDetail />} />
                <Route path="wallet" element={<WalletPage />} />
                <Route path="companies" element={<MyCompanies />} />
                <Route path="companies/:contractId" element={<ContractBasketPage />} />
                <Route path="referral-tree" element={<ReferralTreePage />} />
            </Route>
            <Route path="/customer/profile" element={<ProtectedRoute requiredRole="customer"><CustomerProfileRedirect /></ProtectedRoute>} />
            <Route path="/checkout" element={<ProtectedRoute requiredRole="customer"><CheckoutPage /></ProtectedRoute>} />
            <Route path="/checkout/payment/:orderId" element={<ProtectedRoute requiredRole="customer"><CheckoutPage /></ProtectedRoute>} />
            <Route path="/payment/result" element={<ProtectedRoute requiredRole="customer"><PaymentResultPage /></ProtectedRoute>} />

            {/* Public basket browsing and simulation; adding to cart requires a customer session. */}
            <Route path="/basket-simulator" element={<BasketSimulatorPage />} />

            {/* Inspector */}
            <Route path="/inspector" element={<InspectorRoute><TraceabilityInspectorPage /></InspectorRoute>} />
            <Route path="/orders" element={<Navigate to="/profile/orders" replace />} />
            <Route path="/orders/:orderId" element={<LegacyOrderRedirect />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Super Admin */}
            <Route path="/super-admin" element={<SuperAdminRoute><SuperAdminLayout /></SuperAdminRoute>}>
                <Route index element={<SuperAdminDashboard />} />
                <Route path="dashboard" element={<SuperAdminDashboard />} />
                <Route path="profile" element={<SuperAdminProfile />} />
                <Route path="admins" element={<AdminManagementTable />} />
                <Route path="users" element={<UserManagementTable />} />
                <Route path="vendors" element={<VendorApprovalTable />} />
                <Route path="orders" element={<AdminOrderList />} />
                <Route path="announcements" element={<SmsCampaigns />} />
                <Route path="payments" element={<PaymentsPage />} />
                <Route path="settlements" element={<VendorSettlementsPage />} />
                <Route path="settlements/vendors/:vendorId" element={<VendorSettlementDetailPage />} />
                <Route path="settlements/payouts" element={<PayoutHistoryPage />} />
                <Route path="settlements/refunds" element={<RefundsPage />} />
                <Route path="settlements/shipping" element={<ShippingPayablesPage />} />
                <Route path="settlements/seller-gateways" element={<SellerGatewayAdminPage />} />
                <Route path="settlements/audit" element={<AdminAuditLogPage />} />
                <Route path="referral-simulator" element={<ReferralSimulator />} />
                <Route path="referral-config" element={<ReferralConfigPage />} />
                <Route path="commissions" element={<CommissionsPage />} />
                <Route path="contracts" element={<SuperAdminContractsPage />} />
                <Route path="reports" element={<ReportsLayout />}>
                    <Route index element={<ReportsIndexRedirect />} />
                    <Route path="sales" element={<SalesReport />} />
                    <Route path="products" element={<ProductsReport />} />
                    <Route path="categories" element={<CategoriesReport />} />
                    <Route path="inventory" element={<InventoryReport />} />
                    <Route path="orders" element={<OrdersFunnelReport />} />
                    <Route path="finance" element={<PlatformOnly><FinanceReport /></PlatformOnly>} />
                    <Route path="customers" element={<PlatformOnly><CustomersReport /></PlatformOnly>} />
                    <Route path="vendors" element={<PlatformOnly><VendorsReport /></PlatformOnly>} />
                </Route>
            </Route>

            {/* Irancharsouq Admin */}
            <Route path="/irancharsouq-admin" element={<IrancharsouqAdminRoute><IrancharsouqAdminLayout /></IrancharsouqAdminRoute>}>
                <Route index element={<IrancharsouqAdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="profile" element={<AdminProfile />} />
                <Route path="reports" element={<ReportsLayout />}>
                    <Route index element={<ReportsIndexRedirect />} />
                    <Route path="sales" element={<SalesReport />} />
                    <Route path="products" element={<ProductsReport />} />
                    <Route path="categories" element={<CategoriesReport />} />
                    <Route path="inventory" element={<InventoryReport />} />
                    <Route path="orders" element={<OrdersFunnelReport />} />
                    <Route path="finance" element={<PlatformOnly><FinanceReport /></PlatformOnly>} />
                    <Route path="customers" element={<PlatformOnly><CustomersReport /></PlatformOnly>} />
                    <Route path="vendors" element={<PlatformOnly><VendorsReport /></PlatformOnly>} />
                </Route>
            </Route>

            {/* Vendor */}
            <Route element={<VendorRoute />}>
                <Route path="/vendor" element={<VendorLayout />}>
                    <Route index element={<VendorDashboard />} />
                    <Route path="dashboard" element={<VendorDashboard />} />
                    <Route path="products" element={<VendorProducts />} />
                    <Route path="products/add" element={<VendorProductForm />} />
                    <Route path="products/edit/:id" element={<VendorProductForm />} />
                    <Route path="orders" element={<VendorOrders />} />
                    <Route path="profile" element={<VendorProfile />} />

                    {/* 👈 [ADDED] روت صفحه مبنای محاسبات */}
                    <Route path="pricing-rules" element={<VendorPricingRulesPage />} />

                    <Route element={<VendorReferralRoute />}>
                        <Route path="referral-simulator" element={<VendorReferralSimulator />} />
                        <Route path="referral-configs" element={<VendorReferralConfigsPage />} />
                    </Route>
                    <Route path="packages/create" element={<SimulationProductsPage />} />
                    <Route path="packages/edit/:id" element={<SimulationProductsPage />} />
                    <Route path="packages" element={<VendorPackagesList />} />
                    <Route path="referral-simulator" element={<VendorReferralSimulator />} />
                    <Route path="/vendor/referral-configs" element={<VendorReferralConfigsPage />} />
                    <Route path="contracts" element={<VendorContractsPage />} />
                    <Route path="commissions" element={<VendorCommissionsPage />} />
                    <Route path="reports" element={<VendorReportsPage />} />
                    <Route path="reports/buyers/:packageId" element={<VendorPackageBuyersPage />} />
                </Route>
            </Route>

            {/* Company */}
            <Route element={<CompanyRoute />}>
                <Route path="/company" element={<CompanyLayout />}>
                    <Route index element={<CompanyDashboard />} />
                    <Route path="dashboard" element={<CompanyDashboard />} />
                    <Route path="employees" element={<CompanyEmployees />} />
                    <Route path="employees/add" element={<CompanyAddEmployee />} />
                    <Route path="wallet" element={<CompanyWallet />} />
                    <Route path="contracts" element={<CompanyContracts />} />
                </Route>
            </Route>

        </Routes>
    );
}